/**
 * @fileoverview Syndicator Showdown — Admin Player Stats Bar
 *
 * Three live player stats at the top of the Admin Games dashboard.
 * Each stat is fetched independently — one failing doesn't blank the others.
 *
 * Query design notes:
 *   - Total registered: getCountFromServer with where('role','==','player')
 *     (single-field equality — uses Firestore auto-index, no composite index needed)
 *   - Active now: single where('lastActive','>=',cutoff) + JS role filter
 *     (single-field inequality — avoids the composite-index requirement that
 *     a compound role+lastActive query would impose)
 *   - Played ≥1 game: getDocs on player_entries + client-side userId dedup
 *     (Firestore has no native DISTINCT COUNT; fine at current scale)
 */

import { useState, useEffect } from 'react';
import {
  collection, query, where,
  getDocs, getCountFromServer, Timestamp,
  writeBatch, doc,
} from 'firebase/firestore';
import { db } from '../../services/firebase';

/**
 * Returns a Date representing Monday 00:00:00 of the current week in AEST (UTC+10).
 * Week is defined as Monday–Sunday per Syndicator Showdown business rules.
 */
function getWeekStartAEST() {
  // Work in AEST = UTC+10 (fixed offset, no DST)
  const AEST_OFFSET_MS = 10 * 60 * 60 * 1000;
  const nowAEST = new Date(Date.now() + AEST_OFFSET_MS);

  // getUTCDay() on an AEST-shifted date gives us the AEST day-of-week
  // 0=Sun,1=Mon,...,6=Sat — we want Monday as day 0 of our week
  const dayOfWeek = nowAEST.getUTCDay(); // 0=Sun,1=Mon,...,6=Sat
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  // Rewind to Monday midnight AEST
  const mondayAEST = new Date(nowAEST);
  mondayAEST.setUTCDate(nowAEST.getUTCDate() - daysSinceMonday);
  mondayAEST.setUTCHours(0, 0, 0, 0);

  // Convert back to UTC for Firestore
  return new Date(mondayAEST.getTime() - AEST_OFFSET_MS);
}

const ACTIVE_WINDOW_MINUTES = 15;
const ADMIN_ROLES = new Set(['admin', 'operator']);

export function AdminPlayerStatsBar() {
  const [stats,   setStats]   = useState({
    totalRegistered: null, playedAtLeastOne: null, currentlyActive: null, contact: null,
  });
  const [loading,     setLoading]     = useState(true);
  const [errors,      setErrors]      = useState({});
  const [markingRead, setMarkingRead] = useState(false);

  const handleMarkAllRead = async () => {
    if (!stats.contact?.unreadIds?.length) return;
    setMarkingRead(true);
    try {
      const batch = writeBatch(db);
      stats.contact.unreadIds.forEach(id => {
        batch.update(doc(db, 'contact_submissions', id), { read: true });
      });
      await batch.commit();

      // Optimistically clear unread count in local state
      setStats(prev => ({
        ...prev,
        contact: {
          ...prev.contact,
          unread:    0,
          unreadIds: [],
        },
      }));
    } catch (err) {
      console.error('[AdminPlayerStatsBar] markAllRead failed:', err);
    } finally {
      setMarkingRead(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function loadStats() {
      const usersRef = collection(db, 'users');
      const results  = {};
      const errs     = {};

      // ── 1. Total registered players ────────────────────────────────────────
      try {
        const snap = await getCountFromServer(
          query(usersRef, where('role', '==', 'player'))
        );
        results.totalRegistered = snap.data().count;
      } catch (err) {
        console.error('[AdminPlayerStatsBar] totalRegistered failed:', err);
        errs.totalRegistered = err.message || String(err);
      }

      // ── 2. Currently active (single-field lastActive — no composite index) ─
      try {
        const cutoff = Timestamp.fromMillis(Date.now() - ACTIVE_WINDOW_MINUTES * 60 * 1000);
        const snap   = await getDocs(
          query(usersRef, where('lastActive', '>=', cutoff))
        );
        let count = 0;
        snap.forEach(d => {
          const role = d.data().role || 'player';
          if (!ADMIN_ROLES.has(role)) count++;
        });
        results.currentlyActive = count;
      } catch (err) {
        console.error('[AdminPlayerStatsBar] currentlyActive failed:', err);
        errs.currentlyActive = err.message || String(err);
      }

      // ── 3. Players who've played ≥1 game ───────────────────────────────────
      try {
        const snap          = await getDocs(collection(db, 'player_entries'));
        const uniquePlayers = new Set();
        snap.forEach(d => { const uid = d.data().userId; if (uid) uniquePlayers.add(uid); });
        results.playedAtLeastOne = uniquePlayers.size;
      } catch (err) {
        console.error('[AdminPlayerStatsBar] playedAtLeastOne failed:', err);
        errs.playedAtLeastOne = err.message || String(err);
      }

      // ── 4. Contact submissions ─────────────────────────────────────────────
      try {
        const contactRef = collection(db, 'contact_submissions');
        const now        = new Date();

        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);

        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(todayStart.getDate() - 1);

        const weekStart = getWeekStartAEST();

        const [unreadSnap, todaySnap, yesterdaySnap, weekSnap, unreadDocsSnap] = await Promise.all([
          getCountFromServer(query(contactRef, where('read', '==', false))),
          getCountFromServer(query(contactRef, where('submittedAt', '>=', Timestamp.fromDate(todayStart)))),
          getCountFromServer(query(contactRef,
            where('submittedAt', '>=', Timestamp.fromDate(yesterdayStart)),
            where('submittedAt', '<',  Timestamp.fromDate(todayStart))
          )),
          getCountFromServer(query(contactRef, where('submittedAt', '>=', Timestamp.fromDate(weekStart)))),
          getDocs(query(contactRef, where('read', '==', false))),
        ]);

        results.contact = {
          unread:    unreadSnap.data().count,
          today:     todaySnap.data().count,
          yesterday: yesterdaySnap.data().count,
          week:      weekSnap.data().count,
          unreadIds: unreadDocsSnap.docs.map(d => d.id),
        };
      } catch (err) {
        console.error('[AdminPlayerStatsBar] contact failed:', err);
        errs.contact = err.message || String(err);
      }

      if (!cancelled) {
        setStats(prev => ({ ...prev, ...results }));
        setErrors(errs);
        setLoading(false);
      }
    }

    loadStats();
    return () => { cancelled = true; };
  }, []);

  const fmt = n => (n != null ? n.toLocaleString('en-AU') : '—');

  if (loading) {
    return <div className="ss-player-stats-bar ss-player-stats-bar--loading">Loading player stats…</div>;
  }

  return (
    <div className="ss-player-stats-bar">

      <div className="ss-player-stat">
        <span className="ss-player-stat-value">{fmt(stats.totalRegistered)}</span>
        <span className="ss-player-stat-label">Registered Players</span>
        {errors.totalRegistered && (
          <span className="ss-player-stat-err" title={errors.totalRegistered}>⚠ error</span>
        )}
      </div>

      <div className="ss-player-stat-divider" aria-hidden="true" />

      <div className="ss-player-stat">
        <span className="ss-player-stat-value">{fmt(stats.playedAtLeastOne)}</span>
        <span className="ss-player-stat-label">Played ≥1 Game</span>
        {errors.playedAtLeastOne && (
          <span className="ss-player-stat-err" title={errors.playedAtLeastOne}>⚠ error</span>
        )}
      </div>

      <div className="ss-player-stat-divider" aria-hidden="true" />

      <div className="ss-player-stat">
        <span className="ss-player-stat-value ss-player-stat-value--live">
          {fmt(stats.currentlyActive)}
        </span>
        <span className="ss-player-stat-label">
          Active Now
          <span className="ss-player-stat-window">&nbsp;(last {ACTIVE_WINDOW_MINUTES}min)</span>
        </span>
        {errors.currentlyActive && (
          <span className="ss-player-stat-err" title={errors.currentlyActive}>⚠ error</span>
        )}
      </div>

      <div className="ss-player-stat-divider" aria-hidden="true" />

      {/* Contact submissions — 4th stat */}
      <div className="ss-player-stat ss-player-stat--contact">
        <div className="ss-player-stat-contact-header">
          <span className="ss-player-stat-value">
            ✉️ {stats.contact != null ? fmt(stats.contact.unread) : '—'}
          </span>
          {stats.contact?.unread > 0 && (
            <span className="ss-player-unread-badge">
              {stats.contact.unread} unread
            </span>
          )}
        </div>
        <span className="ss-player-stat-label">Contact Forms</span>

        {stats.contact && (
          <div className="ss-player-contact-breakdown">
            {stats.contact.today > 0 && (
              <span className="ss-player-contact-line ss-player-contact-line--today">
                {stats.contact.today} today
              </span>
            )}
            {stats.contact.yesterday > 0 && (
              <span className="ss-player-contact-line">
                {stats.contact.yesterday} yesterday
              </span>
            )}
            <span className="ss-player-contact-line">
              {stats.contact.week} this week
            </span>
          </div>
        )}

        <div className="ss-player-contact-actions">
          {stats.contact?.unread > 0 && (
            <button
              className="ss-player-check-email"
              onClick={handleMarkAllRead}
              disabled={markingRead}
              type="button"
            >
              {markingRead ? 'Marking read…' : `✓ Mark ${stats.contact.unread} read`}
            </button>
          )}
          <a
            href="https://outlook.cloud.microsoft/mail/inbox"
            target="_blank"
            rel="noopener noreferrer"
            className="ss-player-check-email"
          >
            Open inbox →
          </a>
        </div>

        {errors.contact && (
          <span className="ss-player-stat-err" title={errors.contact}>⚠ error</span>
        )}
      </div>

    </div>
  );
}

export default AdminPlayerStatsBar;
